/** Pure color helpers for the canvas-baked art (no Phaser dependency, so they're unit-testable). */

function channels(color: number): [number, number, number] {
  return [(color >> 16) & 0xff, (color >> 8) & 0xff, color & 0xff];
}

function pack(r: number, g: number, b: number): number {
  const c = (v: number) => Math.max(0, Math.min(255, Math.round(v)));
  return (c(r) << 16) | (c(g) << 8) | c(b);
}

/** Linear blend from `a` (t = 0) to `b` (t = 1). */
export function mix(a: number, b: number, t: number): number {
  const [ar, ag, ab] = channels(a);
  const [br, bg, bb] = channels(b);
  return pack(ar + (br - ar) * t, ag + (bg - ag) * t, ab + (bb - ab) * t);
}

/** Positive `amount` (0..1) lightens toward white, negative darkens toward black. */
export function shade(color: number, amount: number): number {
  return amount >= 0 ? mix(color, 0xffffff, amount) : mix(color, 0x000000, -amount);
}

/** CSS `rgba()` string for Canvas 2D APIs. */
export function css(color: number, alpha = 1): string {
  const [r, g, b] = channels(color);
  return `rgba(${r},${g},${b},${alpha})`;
}

/** Deterministic pseudo-random in [0, 1) from integer inputs, used to place decorations so
 * neighbouring terrain chunks agree on what sits across their shared border. */
export function hash01(a: number, b = 0): number {
  let h = Math.imul(a | 0, 0x27d4eb2d) ^ Math.imul(b | 0, 0x165667b1);
  h = Math.imul(h ^ (h >>> 15), 0x85ebca6b);
  h = Math.imul(h ^ (h >>> 13), 0xc2b2ae35);
  h ^= h >>> 16;
  return (h >>> 0) / 4294967296;
}
